import React, { useCallback } from 'react';
import {
  Button,
  Typography,
  Modal,
  Box,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import CloseIconButton from '../../../../components/common/CloseIconButton';
import { useTranslation } from 'react-i18next';
import {
  modalBoxSx,
  closeButtonWrapperSx,
  gridContainerSx,
  iconBoxSx,
  titleSx,
  messageSx,
  actionsBoxSx,
  refundButtonSx,
  advanceButtonSx,
} from './styles';

interface SalesReturnSettleDialogProps {
  open: boolean;
  onClose: () => void;
  onAddToAdvance: () => void;
  onRefundToAdvance: () => void;
  title: string;
  message: string;
  icon?: JSX.Element;
}

/**
 * Accessible, testable, modern dialog presenting refund/advance options for sales return.
 */
const SalesReturnSettleDialog: React.FC<SalesReturnSettleDialogProps> = ({
  open,
  onClose,
  onAddToAdvance,
  onRefundToAdvance,
  title,
  message,
  icon,
}) => {
  const { t } = useTranslation('sales_return');
  
  // Ensure handlers are stable across renders/tests
  const handleRefundToAdvance = useCallback(() => {
    onRefundToAdvance();
  }, [onRefundToAdvance]);

  const handleAddToAdvance = useCallback(() => {
    onAddToAdvance();
  }, [onAddToAdvance]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="sales-return-settle-dialog-title"
      aria-describedby="sales-return-settle-dialog-message"
      id="sales-return-settle-dialog"
      data-testid="sales-return-settle-dialog"
    >
      <Box sx={modalBoxSx} id="sales-return-settle-dialog-box" data-testid="sales-return-settle-dialog-box" role="dialog">
        {/* Close button at top-right corner */}
        <Box
          sx={closeButtonWrapperSx}
          id="sales-return-settle-dialog-close-btn-wrapper"
          data-testid="sales-return-settle-dialog-close-btn-wrapper"
        >
          <CloseIconButton
            onClick={handleClose}
            aria-label="Close sales return settle dialog"
            id="sales-return-settle-dialog-close-btn"
            data-testid="sales-return-settle-dialog-close-btn"
          />
        </Box>

        <Grid2
          container
          spacing={2}
          sx={gridContainerSx}
          id="sales-return-settle-dialog-content-grid"
          data-testid="sales-return-settle-dialog-content-grid"
        >
          {icon && (
            <Grid2
              size={12}
              id="sales-return-settle-dialog-icon-grid"
              data-testid="sales-return-settle-dialog-icon-grid"
            >
              <Box
                sx={iconBoxSx}
                id="sales-return-settle-dialog-icon-box"
                data-testid="sales-return-settle-dialog-icon-box"
              >
                {icon}
              </Box>
            </Grid2>
          )}
          <Grid2
            size={12}
            id="sales-return-settle-dialog-title-grid"
            data-testid="sales-return-settle-dialog-title-grid"
          >
            <Typography
              sx={titleSx}
              id="sales-return-settle-dialog-title"
              data-testid="sales-return-settle-dialog-title"
              variant="h6"
            >
              {title}
            </Typography>
          </Grid2>
          <Grid2
            size={12}
            id="sales-return-settle-dialog-message-grid"
            data-testid="sales-return-settle-dialog-message-grid"
          >
            <Typography
              component="div"
              sx={messageSx}
              id="sales-return-settle-dialog-message"
              data-testid="sales-return-settle-dialog-message"
              role="document"
            >
              {message}
            </Typography>
          </Grid2>
        </Grid2>
        <Box
          sx={actionsBoxSx}
          id="sales-return-settle-dialog-actions-box"
          data-testid="sales-return-settle-dialog-actions-box"
        >
          <Button
            variant="outlined"
            onClick={handleRefundToAdvance}
            sx={refundButtonSx}
            id="sales-return-settle-dialog-refund-btn"
            data-testid="sales-return-settle-dialog-refund-btn"
            aria-label={t('Button_Refund_To_Patient')}
            tabIndex={0}
          >
            {t('Button_Refund_To_Patient')}
          </Button>
          <Button
            variant="contained"
            onClick={handleAddToAdvance}
            sx={advanceButtonSx}
            id="sales-return-settle-dialog-advance-btn"
            data-testid="sales-return-settle-dialog-advance-btn"
            aria-label={t('Button_Add_To_Advance')}
            tabIndex={0}
          >
            {t('Button_Add_To_Advance')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SalesReturnSettleDialog;